import React from 'react';
import { Box } from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { DOCUMENT } from '../constants/documentTheme';
import { getItemMapsUrl, buildLocationMapsUrl } from '../utils/itineraryDocumentUtils';

/**
 * Renders a location as a Google Maps link so travellers can get directions.
 * Implemented as a span (not an anchor) so it is safe to nest inside the
 * itinerary/reservation cards that are themselves rendered as <button>s.
 */
const MapLink = ({
  item = null,
  location = null,
  url = null,
  fontSize = 13.5,
  iconSize = 14,
  sx = {},
}) => {
  const label = location || item?.location || '';
  const resolvedUrl = url || (item ? getItemMapsUrl(item) : buildLocationMapsUrl(location));

  if (!label) return null;

  if (!resolvedUrl) {
    return (
      <Box component="span" sx={{ fontSize, color: DOCUMENT.ink2, ...sx }}>
        {label}
      </Box>
    );
  }

  const open = (event) => {
    event.stopPropagation();
    event.preventDefault();
    window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      component="span"
      role="link"
      tabIndex={0}
      data-testid="map-link"
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          open(event);
        }
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize,
        color: DOCUMENT.ink2,
        cursor: 'pointer',
        textDecoration: 'none',
        transition: 'color .15s ease',
        '&:hover .map-link-label': { color: DOCUMENT.accent, textDecoration: 'underline' },
        '&:focus-visible': {
          outline: `1px solid ${DOCUMENT.accent}`,
          outlineOffset: 2,
        },
        ...sx,
        // Keep link width stable even if callers pass width/maxWidth via sx
        width: 'fit-content',
        maxWidth: '100%',
      }}
    >
      <PlaceOutlinedIcon
        className="map-link-icon"
        sx={{ fontSize: iconSize, color: DOCUMENT.ink3, flexShrink: 0 }}
      />
      <Box component="span" className="map-link-label" title={label}>{label}</Box>
    </Box>
  );
};

export default MapLink;
